/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useRef, useState } from "react";
import Label from "../Label";
import { useController } from "react-hook-form";
import styled from "styled-components";
import { StyledFormField } from "./StyledFormField";
import { useTranslate } from "../../../hooks/useTranslate";

const InputPrefix = styled.span`
  display: flex;
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
`;

const InputSuffix = styled.span`
  display: flex;
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
`;

const InputWrapper = styled.div`
  position: relative;
`;

const TextField = React.forwardRef(
  (
    {
      name,
      control,
      label,
      required = false,
      defaultValue,
      validationRules = {},
      prefix,
      suffix,
      placeholder,
      ...props
    },
    ref
  ) => {
    const { _t } = useTranslate();
    const { field, fieldState } = useController({
      name,
      control,
      rules: {
        required: required ? "error_value_is_empty" : undefined,
        ...validationRules,
      },
      defaultValue: defaultValue || "",
    });

    const prefixRef = useRef(null);
    const suffixRef = useRef(null);
    const [inputPadding, setInputPadding] = useState({ paddingLeft: "16px", paddingRight: "16px" });

    useEffect(() => {
      const calculatePadding = () => {
        const prefixWidth = prefixRef.current?.offsetWidth || 0;
        const suffixWidth = suffixRef.current?.offsetWidth || 0;

        setInputPadding({
          paddingLeft: prefix ? `${prefixWidth + 20}px` : "16px",
          paddingRight: suffix ? `${suffixWidth + 20}px` : "16px",
        });
      };

      calculatePadding();
    }, [prefix, suffix]);

    return (
      <>
        {label ? (
          <Label name={name} required={required}>
            {label}
          </Label>
        ) : undefined}
        <InputWrapper>
          {prefix && <InputPrefix ref={prefixRef}>{prefix}</InputPrefix>}
          <StyledFormField.Input
            {...field}
            id={name}
            type="text"
            $error={fieldState.error}
            aria-invalid={!!fieldState.error}
            aria-required={required || undefined}
            aria-describedby={fieldState.error ? `${name}-error` : undefined}
            $inputPadding={inputPadding}
            ref={ref}
            placeholder={_t(placeholder)}
            $hasPrefix={!!prefix}
            $hasSuffix={!!suffix}
            {...props}
          />
          {suffix && <InputSuffix ref={suffixRef}>{suffix}</InputSuffix>}
        </InputWrapper>
      </>
    );
  }
);

export default TextField;
